import React from 'react';
import { Link } from 'react-router-dom';

const Privacy: React.FC = () => {
  return (
    <div className='min-h-screen bg-gray-50'>
      <header className='bg-white shadow-sm'>
        <div className='container mx-auto px-4 py-6'>
          <Link
            to='/'
            className='text-2xl font-bold text-primary hover:text-orange-600'
          >
            ← StarJar
          </Link>
        </div>
      </header>

      <main className='container mx-auto px-4 py-12 max-w-4xl'>
        <h1 className='text-4xl font-bold mb-8 text-gray-900'>
          Privacy Policy
        </h1>

        <div className='bg-white rounded-lg shadow-md p-8 space-y-6 text-gray-700'>
          <p className='text-sm text-gray-500'>
            Last updated: October 19, 2025
          </p>

          <section>
            <h2 className='text-2xl font-bold mb-4 text-gray-900'>
              Introduction
            </h2>
            <p>
              StarJar ("we", "our", or "us") is committed to protecting your
              privacy. This Privacy Policy explains how we collect, use, and
              safeguard your information when you use our mobile application.
            </p>
          </section>

          <section>
            <h2 className='text-2xl font-bold mb-4 text-gray-900'>
              Information We Collect
            </h2>
            <h3 className='text-xl font-semibold mb-2 text-gray-800'>
              Data You Provide
            </h3>
            <ul className='list-disc pl-6 space-y-2'>
              <li>Jar names and profiles you create</li>
              <li>Behaviors and rewards you define</li>
              <li>Star transactions and redemption history</li>
              <li>
                A parental PIN, if you choose to set one (kept on your device,
                never sent to us)
              </li>
            </ul>

            <h3 className='text-xl font-semibold mb-2 mt-4 text-gray-800'>
              Automatically Collected Data
            </h3>
            <ul className='list-disc pl-6 space-y-2'>
              <li>
                Device information (iOS version, device model, language and
                region)
              </li>
              <li>
                How the app is used — for example which screens are opened, when
                stars are awarded, and whether a purchase succeeds. These
                records hold counts only. They never include the names you give
                your jars, behaviours or rewards, any note you write, or
                anything identifying an individual child.
              </li>
              <li>Crash reports and diagnostic information</li>
              <li>
                A random identifier for your app installation, used to group
                this activity together. It is not your name, not your Apple
                Account, and not the advertising identifier.
              </li>
            </ul>
          </section>

          <section>
            <h2 className='text-2xl font-bold mb-4 text-gray-900'>
              How We Use Your Information
            </h2>
            <p>We use the collected information to:</p>
            <ul className='list-disc pl-6 space-y-2 mt-2'>
              <li>Provide and maintain the StarJar service</li>
              <li>Sync your data across your devices via iCloud</li>
              <li>Process subscription payments through Apple</li>
              <li>Improve app functionality and user experience</li>
              <li>Understand which features are used, so we know what to improve</li>
              <li>Diagnose crashes and fix them</li>
              <li>Send important updates about the service</li>
              <li>Provide customer support</li>
            </ul>
          </section>

          <section>
            <h2 className='text-2xl font-bold mb-4 text-gray-900'>
              Data Storage and Security
            </h2>
            <p className='mb-2'>
              <strong>Local Storage:</strong> All your data is stored locally on
              your device using SwiftData.
            </p>
            <p className='mb-2'>
              <strong>iCloud Sync:</strong> If you enable iCloud sync, your data
              is encrypted and synchronized through Apple's iCloud service. We
              do not have direct access to your iCloud data.
            </p>
            <p className='mb-2'>
              <strong>PIN Protection:</strong> Your PIN is stored on your device
              and is never sent to us or to anyone else, so we cannot access or
              recover it. It is there to stop a child changing stars or
              settings; it is not designed to withstand someone who has your
              unlocked device or its backups.
            </p>
            <p>
              <strong>Security Measures:</strong> We implement industry-standard
              security measures to protect your data from unauthorized access,
              alteration, or destruction.
            </p>
          </section>

          <section>
            <h2 className='text-2xl font-bold mb-4 text-gray-900'>
              Third-Party Services
            </h2>
            <p>StarJar uses the following third-party services:</p>
            <ul className='list-disc pl-6 space-y-2 mt-2'>
              <li>
                <strong>Apple iCloud:</strong> For optional data synchronization
                across your devices
              </li>
              <li>
                <strong>Apple StoreKit:</strong> For processing in-app purchases
                and subscriptions
              </li>
              <li>
                <strong>Google Firebase:</strong> Analytics, to understand how
                the app is used, and Crashlytics, to receive crash reports.
                Google processes this data on our behalf. It contains none of
                the names you have entered.
              </li>
            </ul>
            <p className='mt-2'>
              These services have their own privacy policies. We never sell your
              data, and we do not share it with anyone beyond the services
              listed here.
            </p>
          </section>

          <section>
            <h2 className='text-2xl font-bold mb-4 text-gray-900'>
              Children's Privacy
            </h2>
            <p>
              StarJar is designed for family use. While children may use the app
              under parental supervision, we do not knowingly collect personal
              information directly from children under 13. Parents and guardians
              are responsible for managing their children's profiles and data
              within the app.
            </p>
            <p className='mt-2'>
              StarJar has no accounts and no logins, and children do not sign
              in. The usage and crash data described above is tied to the app
              installation rather than to a person, and never includes the names
              of jars, behaviours or rewards. We do not use it for advertising,
              and we do not track you across other apps or websites.
            </p>
          </section>

          <section>
            <h2 className='text-2xl font-bold mb-4 text-gray-900'>
              Data Retention and Deletion
            </h2>
            <p>
              Your data is retained on your device and iCloud (if enabled) until
              you choose to delete it. You can:
            </p>
            <ul className='list-disc pl-6 space-y-2 mt-2'>
              <li>
                Delete individual items (jars, behaviors, rewards, transactions)
              </li>
              <li>Export your data before deletion (Premium feature)</li>
              <li>Reset all data from the Settings menu</li>
              <li>Delete the app to remove all local data</li>
              <li>Disable iCloud sync to remove data from iCloud</li>
            </ul>
          </section>

          <section>
            <h2 className='text-2xl font-bold mb-4 text-gray-900'>
              Your Rights
            </h2>
            <p>You have the right to:</p>
            <ul className='list-disc pl-6 space-y-2 mt-2'>
              <li>Access your personal data</li>
              <li>Correct inaccurate data</li>
              <li>Delete your data</li>
              <li>Export your data (Premium feature)</li>
              <li>Opt-out of data collection (by not using the app)</li>
            </ul>
          </section>

          <section>
            <h2 className='text-2xl font-bold mb-4 text-gray-900'>
              Changes to This Policy
            </h2>
            <p>
              We may update this Privacy Policy from time to time. We will
              notify you of any changes by posting the new Privacy Policy on
              this page and updating the "Last updated" date.
            </p>
          </section>

          <section>
            <h2 className='text-2xl font-bold mb-4 text-gray-900'>
              Contact Us
            </h2>
            <p>
              If you have questions about this Privacy Policy, please contact us
              at:
            </p>
            <p className='mt-2'>
              <a
                href='mailto:starjar@twinkle.hk'
                className='text-primary hover:text-orange-600 font-semibold'
              >
                starjar@twinkle.hk
              </a>
            </p>
          </section>
        </div>

        <div className='mt-8 text-center'>
          <Link
            to='/'
            className='text-primary hover:text-orange-600 font-semibold'
          >
            ← Back to Home
          </Link>
        </div>
      </main>

      <footer className='bg-gray-900 text-white py-8 mt-12'>
        <div className='container mx-auto px-4 text-center'>
          <p className='text-gray-400'>© 2025 StarJar. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Privacy;
